import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  Divider,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import PricingDecisionExistingPairWorkspace
  from './PricingDecisionExistingPairWorkspace';

import {
  getMarginCenterProductCustomers,
} from '../services/marginCenterService';

import CustomerRelationshipWorkspace
  from './CustomerRelationshipWorkspace';

import ProductCustomerPortfolioAnalysisPanel
  from './ProductCustomerPortfolioAnalysisPanel';

import type {
  MarginCenterComparisonMode,
  MarginCenterProductCustomerRow,
  MarginCenterProductCustomerSortDirection,
  MarginCenterProductCustomerSortField,
  MarginCenterProductCustomersParams,
  MarginCenterTimeGrain,
} from '../types/marginCenter';
import { pricingNumberFormat } from '../../../modules/pricingJourney/formatNumber';


const SORT_FIELDS:
  MarginCenterProductCustomerSortField[] = [
    'customer_label',
    'revenue',
    'revenue_delta',
    'revenue_delta_pct',
    'revenue_share_pct',
    'revenue_share_delta_points',
    'margin_amount',
    'margin_percentage',
    'margin_percentage_delta_points',
    'units',
    'units_delta_pct',
    'average_net_unit_price',
    'average_net_unit_price_delta_pct',
    'active_days',
    'active_days_delta',
    'days_since_last_sale',
    'last_sale_date',
  ];

const COMPARISON_SORT_FIELDS =
  new Set<MarginCenterProductCustomerSortField>([
    'revenue_delta',
    'revenue_delta_pct',
    'revenue_share_delta_points',
    'margin_percentage_delta_points',
    'units_delta_pct',
    'average_net_unit_price_delta_pct',
    'active_days_delta',
  ]);

const numberFormatter = pricingNumberFormat({
  maximumFractionDigits: 2,
});

const integerFormatter = pricingNumberFormat({
  maximumFractionDigits: 0,
});


function formatNumber(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) return '—';

  return numberFormatter.format(value);
}


function formatInteger(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) return '—';

  return integerFormatter.format(value);
}


function formatPct(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) return '—';

  return `${numberFormatter.format(value)} %`;
}


function formatSignedPct(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) return '—';

  return `${value > 0 ? '+' : ''}${numberFormatter.format(value)} %`;
}


function formatSignedPoints(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) return '—';

  return `${value > 0 ? '+' : ''}${numberFormatter.format(value)} pp`;
}


function formatCurrency(
  value: number | null | undefined,
  currencyCode: string | null,
): string {
  if (value === null || value === undefined) return '—';

  if (!currencyCode) return formatNumber(value);

  try {
    return pricingNumberFormat({
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return formatNumber(value);
  }
}


function formatSignedCurrency(
  value: number | null | undefined,
  currencyCode: string | null,
): string {
  if (value === null || value === undefined) return '—';

  const formatted = formatCurrency(
    Math.abs(value),
    currencyCode,
  );

  if (value > 0) return `+${formatted}`;
  if (value < 0) return `-${formatted}`;

  return formatted;
}


function formatDate(
  value: string | null | undefined,
): string {
  if (!value) return '—';

  const [year, month, day] = value
    .slice(0, 10)
    .split('-')
    .map(Number);

  if (!year || !month || !day) return value;

  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(year, month - 1, day));
}


function getDirectionalColor(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined || value === 0) {
    return '#94a3b8';
  }

  return value > 0 ? '#34d399' : '#fb7185';
}


function readSortField(
  value: string | null,
  hasComparison: boolean,
): MarginCenterProductCustomerSortField {
  const fallback: MarginCenterProductCustomerSortField =
    hasComparison ? 'revenue_delta' : 'revenue';

  if (
    value
    && SORT_FIELDS.includes(
      value as MarginCenterProductCustomerSortField,
    )
  ) {
    const candidate =
      value as MarginCenterProductCustomerSortField;

    if (
      !hasComparison
      && COMPARISON_SORT_FIELDS.has(candidate)
    ) {
      return fallback;
    }

    return candidate;
  }

  return fallback;
}


function readSortDirection(
  value: string | null,
): MarginCenterProductCustomerSortDirection {
  return value === 'asc' ? 'asc' : 'desc';
}


function SummaryMetric({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        minHeight: 102,
        borderRadius: 2.25,
        border: '1px solid rgba(148,163,184,0.14)',
        bgcolor: 'rgba(2,6,23,0.24)',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: '#94a3b8',
          fontWeight: 750,
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="h6"
        sx={{
          mt: 0.3,
          color: '#f8fafc',
          fontWeight: 850,
        }}
      >
        {value}
      </Typography>

      {detail && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.2,
            color: '#64748b',
            lineHeight: 1.35,
          }}
        >
          {detail}
        </Typography>
      )}
    </Paper>
  );
}


function SituationCard({
  title,
  value,
  detail,
  actionLabel,
  onAction,
}: {
  title: string;
  value: string;
  detail: string;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.6,
        minHeight: 160,
        borderRadius: 2.25,
        border: '1px solid rgba(148,163,184,0.14)',
        bgcolor: 'rgba(2,6,23,0.26)',
      }}
    >
      <Stack spacing={0.8} sx={{ height: '100%' }}>
        <Typography
          variant="overline"
          sx={{
            color: '#64748b',
            fontSize: '0.68rem',
            fontWeight: 800,
            letterSpacing: 0.9,
            lineHeight: 1.4,
          }}
        >
          Hecho observado
        </Typography>

        <Typography
          variant="subtitle2"
          sx={{
            color: '#cbd5e1',
            fontWeight: 800,
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="h6"
          sx={{
            color: '#f8fafc',
            fontWeight: 850,
          }}
        >
          {value}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: '#94a3b8',
            lineHeight: 1.5,
            flexGrow: 1,
          }}
        >
          {detail}
        </Typography>

        <Button
          size="small"
          variant="text"
          onClick={onAction}
          sx={{
            alignSelf: 'flex-start',
            px: 0,
            minWidth: 0,
            textTransform: 'none',
            fontWeight: 750,
          }}
        >
          {actionLabel}
        </Button>
      </Stack>
    </Paper>
  );
}


function SortableHeader({
  field,
  label,
  activeField,
  direction,
  onSort,
  align = 'right',
  help,
}: {
  field: MarginCenterProductCustomerSortField;
  label: string;
  activeField: MarginCenterProductCustomerSortField;
  direction: MarginCenterProductCustomerSortDirection;
  onSort: (
    field: MarginCenterProductCustomerSortField,
  ) => void;
  align?: 'left' | 'right';
  help?: string;
}) {
  const active = activeField === field;

  return (
    <TableCell
      align={align}
      sortDirection={active ? direction : false}
      sx={{
        whiteSpace: 'nowrap',
        bgcolor: '#0f172a',
        color: '#cbd5e1',
        fontWeight: 800,
      }}
    >
      <Stack
        direction="row"
        spacing={0.45}
        alignItems="center"
        justifyContent={
          align === 'left'
            ? 'flex-start'
            : 'flex-end'
        }
      >
        <TableSortLabel
          active={active}
          direction={active ? direction : 'asc'}
          onClick={() => onSort(field)}
          sx={{
            color: '#cbd5e1',
            '&.Mui-active': {
              color: '#38bdf8',
            },
            '& .MuiTableSortLabel-icon': {
              color: active
                ? '#38bdf8'
                : '#64748b',
            },
          }}
        >
          {label}
        </TableSortLabel>

        {help && (
          <Tooltip
            title={help}
            placement="top"
            arrow
          >
            <Box
              component="span"
              aria-label={`Ayuda sobre ${label}`}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                color: '#64748b',
                cursor: 'help',
              }}
            >
              <InfoOutlinedIcon
                sx={{ fontSize: 14 }}
              />
            </Box>
          </Tooltip>
        )}
      </Stack>
    </TableCell>
  );
}


function MetricCell({
  value,
  comparison,
  change,
  changeValue,
}: {
  value: string;
  comparison?: string;
  change?: string;
  changeValue?: number | null;
}) {
  return (
    <TableCell
      align="right"
      sx={{
        whiteSpace: 'nowrap',
      }}
    >
      <Stack
        spacing={0.1}
        alignItems="flex-end"
      >
        <Stack
          direction="row"
          spacing={0.55}
          alignItems="baseline"
          justifyContent="flex-end"
        >
          <Typography
            variant="body2"
            sx={{
              color: '#e2e8f0',
              fontWeight: 800,
            }}
          >
            {value}
          </Typography>

          {comparison !== undefined && (
            <>
              <Typography
                component="span"
                variant="body2"
                sx={{
                  color: '#475569',
                  fontWeight: 700,
                }}
              >
                /
              </Typography>

              <Typography
                component="span"
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontWeight: 700,
                }}
              >
                {comparison}
              </Typography>
            </>
          )}
        </Stack>

        {change !== undefined && (
          <Typography
            variant="caption"
            sx={{
              color:
                getDirectionalColor(
                  changeValue,
                ),
              fontWeight: 850,
            }}
          >
            {change}
          </Typography>
        )}
      </Stack>
    </TableCell>
  );
}


function ActivityCell({
  row,
  hasComparison,
}: {
  row: MarginCenterProductCustomerRow;
  hasComparison: boolean;
}) {
  const activeDaysDelta =
    row.changes?.active_days_delta;

  const activeDaysDeltaLabel =
    activeDaysDelta === null
    || activeDaysDelta === undefined
      ? null
      : `${
          activeDaysDelta > 0
            ? '+'
            : ''
        }${formatInteger(
          activeDaysDelta,
        )}`;

  return (
    <TableCell
      align="right"
      sx={{
        minWidth: 205,
        whiteSpace: 'nowrap',
      }}
    >
      <Stack
        spacing={0.1}
        alignItems="flex-end"
      >
        <Stack
          direction="row"
          spacing={0.45}
          alignItems="baseline"
          justifyContent="flex-end"
        >
          <Typography
            variant="body2"
            sx={{
              color: '#e2e8f0',
              fontWeight: 800,
            }}
          >
            {formatDate(
              row.activity.last_sale_date,
            )}
          </Typography>

          {row.activity.days_since_last_sale
            !== null && (
            <Typography
              variant="caption"
              sx={{
                color: '#64748b',
                fontWeight: 700,
              }}
            >
              · {formatInteger(
                row.activity.days_since_last_sale,
              )} d
            </Typography>
          )}
        </Stack>

        <Typography
          variant="caption"
          sx={{
            color: '#94a3b8',
            fontWeight: 700,
          }}
        >
          {formatInteger(
            row.current.active_days,
          )}
          {hasComparison
            ? `/${formatInteger(
                row.comparison?.active_days,
              )}`
            : ''} días
          {hasComparison
            && activeDaysDeltaLabel
              ? ` (${activeDaysDeltaLabel})`
              : ''}
          {' · '}
          {formatInteger(
            row.current.transaction_count,
          )}
          {hasComparison
            ? `/${formatInteger(
                row.comparison
                  ?.transaction_count,
              )}`
            : ''} tx
        </Typography>
      </Stack>
    </TableCell>
  );
}


function QuickSortButton({
  label,
  field,
  direction,
  activeField,
  activeDirection,
  disabled = false,
  onSelect,
}: {
  label: string;

  field:
    MarginCenterProductCustomerSortField;

  direction:
    MarginCenterProductCustomerSortDirection;

  activeField:
    MarginCenterProductCustomerSortField;

  activeDirection:
    MarginCenterProductCustomerSortDirection;

  disabled?: boolean;

  onSelect: (
    field:
      MarginCenterProductCustomerSortField,
    direction:
      MarginCenterProductCustomerSortDirection,
  ) => void;
}) {
  const selected =
    activeField === field
    && activeDirection === direction;

  return (
    <Button
      size="small"
      variant="outlined"
      disabled={disabled}
      aria-pressed={selected}
      onClick={() =>
        onSelect(
          field,
          direction,
        )
      }
      sx={{
        minHeight: 32,
        px: 1.3,
        borderRadius: 2,
        textTransform: 'none',
        fontWeight: 750,

        color: selected
          ? '#e0f2fe'
          : '#cbd5e1',

        borderColor: selected
          ? '#38bdf8'
          : 'rgba(148,163,184,0.22)',

        bgcolor: selected
          ? 'rgba(56,189,248,0.10)'
          : 'rgba(15,23,42,0.18)',

        '&:hover': {
          borderColor: '#38bdf8',
          bgcolor: selected
            ? 'rgba(56,189,248,0.14)'
            : 'rgba(56,189,248,0.06)',
        },

        '&.Mui-disabled': {
          color: '#475569',
          borderColor:
            'rgba(148,163,184,0.10)',
        },
      }}
    >
      {label}
    </Button>
  );
}


function CustomerCell({
  row,
}: {
  row: MarginCenterProductCustomerRow;
}) {
  const context = [
    row.customer.attributes.customer_segment,
    row.customer.attributes.country,
  ].filter(Boolean) as string[];

  return (
    <TableCell sx={{ minWidth: 280 }}>
      <Typography
        variant="body2"
        sx={{
          color: '#f8fafc',
          fontWeight: 800,
        }}
      >
        {row.customer.label}
      </Typography>

      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: '#64748b',
        }}
      >
        {row.customer.customer_id}
        {context.length > 0 ? ` · ${context.join(' · ')}` : ''}
      </Typography>
    </TableCell>
  );
}


export default function ProductCustomersDecisionPanel({
  productId,
  startDate,
  endDate,
  comparisonMode,
  comparisonStartDate,
  comparisonEndDate,
  timeGrain,
  currencyCode,
}: {
  productId: string;
  startDate: string | null;
  endDate: string | null;
  comparisonMode: MarginCenterComparisonMode;
  comparisonStartDate: string | null;
  comparisonEndDate: string | null;
  timeGrain: MarginCenterTimeGrain;
  currencyCode: string | null;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const [decisionWorkspaceOpen, setDecisionWorkspaceOpen] =
    useState(false);

  const hasComparison = comparisonMode !== 'none';

  const sortField = readSortField(
    searchParams.get('productCustomerSort'),
    hasComparison,
  );

  const sortDirection = readSortDirection(
    searchParams.get('productCustomerDirection'),
  );

  const appliedSearch =
    searchParams.get('productCustomerSearch')?.trim() ?? '';

  const cursor =
    searchParams.get('productCustomerCursor')?.trim()
    || undefined;

  const [searchInput, setSearchInput] = useState(appliedSearch);

  useEffect(() => {
    setSearchInput(appliedSearch);
  }, [appliedSearch]);

  useEffect(() => {
    const normalized = searchInput.trim();

    if (normalized === appliedSearch) return;

    const timer = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);

      if (normalized) {
        next.set('productCustomerSearch', normalized);
      } else {
        next.delete('productCustomerSearch');
      }

      next.delete('productCustomerCursor');
      next.delete('relationshipCustomer');

      setCursorHistory([]);
      setSearchParams(next, { replace: true });
    }, 350);

    return () => window.clearTimeout(timer);
  }, [
    appliedSearch,
    searchInput,
    searchParams,
    setSearchParams,
  ]);

  const queryParams = useMemo<MarginCenterProductCustomersParams>(
    () => ({
      start_date: startDate ?? undefined,
      end_date: endDate ?? undefined,
      comparison_mode: comparisonMode,
      comparison_start_date:
        comparisonMode === 'custom'
          ? comparisonStartDate ?? undefined
          : undefined,
      comparison_end_date:
        comparisonMode === 'custom'
          ? comparisonEndDate ?? undefined
          : undefined,
      search: appliedSearch || undefined,
      sort_field: sortField,
      sort_direction: sortDirection,
      limit: 50,
      cursor,
    }),
    [
      appliedSearch,
      comparisonEndDate,
      comparisonMode,
      comparisonStartDate,
      cursor,
      endDate,
      sortDirection,
      sortField,
      startDate,
    ],
  );

  const customersQuery = useQuery({
    queryKey: [
      'margin-center',
      'product-customers',
      productId,
      queryParams,
    ],
    queryFn: () =>
      getMarginCenterProductCustomers(
        productId,
        queryParams,
      ),
    placeholderData: (previousData) => previousData,
  });

  const data = customersQuery.data;

  const portfolioAnalysis =
    data?.portfolio_analysis ?? null;

  const summary = data?.portfolio_summary;
  const rows = data?.rows ?? [];

  const setAnalysisState = (
    changes: Record<string, string | null>,
  ) => {
    const next = new URLSearchParams(searchParams);

    Object.entries(changes).forEach(([key, value]) => {
      if (value === null || value === '') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });

    next.delete('productCustomerCursor');
    next.delete('relationshipCustomer');

    setCursorHistory([]);
    setSearchParams(next);
  };

  const handleSort = (
    field: MarginCenterProductCustomerSortField,
  ) => {
    if (!hasComparison && COMPARISON_SORT_FIELDS.has(field)) {
      return;
    }

    const direction: MarginCenterProductCustomerSortDirection =
      sortField === field
        ? sortDirection === 'asc'
          ? 'desc'
          : 'asc'
        : field === 'customer_label'
          || field === 'last_sale_date'
          ? 'asc'
          : 'desc';

    setAnalysisState({
      productCustomerSort: field,
      productCustomerDirection: direction,
    });
  };

  const nextPage = () => {
    const nextCursor = data?.page.next_cursor;

    if (!nextCursor) return;

    setCursorHistory((current) => [
      ...current,
      searchParams.get('productCustomerCursor') ?? '',
    ]);

    const next = new URLSearchParams(searchParams);
    next.set('productCustomerCursor', nextCursor);
    next.delete('relationshipCustomer');
    setSearchParams(next);
  };

  const previousPage = () => {
    if (cursorHistory.length === 0) return;

    const previousCursor =
      cursorHistory[cursorHistory.length - 1];

    setCursorHistory((current) => current.slice(0, -1));

    const next = new URLSearchParams(searchParams);

    if (previousCursor) {
      next.set('productCustomerCursor', previousCursor);
    } else {
      next.delete('productCustomerCursor');
    }

    next.delete('relationshipCustomer');
    setSearchParams(next);
  };

  const selectedRelationshipCustomerId =
    searchParams.get('relationshipCustomer')?.trim()
    || null;

  const openRelationship = (
    customerId: string,
  ) => {
    const next = new URLSearchParams(searchParams);
    next.delete('relationshipProduct');
    next.set('relationshipCustomer', customerId);
    setSearchParams(next);
  };

  const closeRelationship = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('relationshipCustomer');
    setSearchParams(next, { replace: true });
  };

  const largestGrowth =
    data?.highlights.largest_revenue_growth[0];

  const largestDecline =
    data?.highlights.largest_revenue_decline[0];

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: '1px solid rgba(148,163,184,0.18)',
        bgcolor: 'rgba(15,23,42,0.54)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: { xs: 2, md: 2.5 } }}>
        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          spacing={1.5}
          alignItems={{
            xs: 'stretch',
            sm: 'flex-start',
          }}
          justifyContent="space-between"
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                color: '#f8fafc',
                fontWeight: 850,
              }}
            >
              Clientes del producto
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: '#94a3b8',
                maxWidth: 920,
                lineHeight: 1.6,
              }}
            >
              Identifica qué clientes explican los cambios del
              producto y abre cualquier relación concreta sin
              perder el periodo ni la comparación de origen.
            </Typography>
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={() => setDecisionWorkspaceOpen(true)}
            sx={{ flexShrink: 0 }}
          >
            Evaluar otro cliente
          </Button>
        </Stack>

        {data?.status === 'ok'
          && portfolioAnalysis && (
          <ProductCustomerPortfolioAnalysisPanel
            analysis={portfolioAnalysis}
            productId={productId}
            startDate={startDate}
            endDate={endDate}
            comparisonMode={comparisonMode}
            comparisonStartDate={
              comparisonStartDate
            }
            comparisonEndDate={
              comparisonEndDate
            }
            currencyCode={currencyCode}
            hasComparison={hasComparison}
            onOpenRelationship={openRelationship}
          />
        )}

        {!portfolioAnalysis && summary && (
          <Box
            sx={{
              mt: 2,
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(3, minmax(0, 1fr))',
                xl: 'repeat(6, minmax(0, 1fr))',
              },
              gap: 1,
            }}
          >
            <SummaryMetric
              label="Clientes activos"
              value={formatInteger(
                summary.current_active_customers,
              )}
              detail={
                summary.comparison_active_customers !== null
                  ? `${formatInteger(
                      summary.comparison_active_customers,
                    )} en comparación`
                  : undefined
              }
            />

            <SummaryMetric
              label="Mayor cliente"
              value={formatPct(
                summary.current_top_customer_revenue_share_pct,
              )}
              detail="Peso sobre ingresos actuales"
            />

            <SummaryMetric
              label="Top 5"
              value={formatPct(
                summary.current_top_5_revenue_share_pct,
              )}
              detail="Peso sobre ingresos actuales"
            />

            {hasComparison && (
              <SummaryMetric
                label="Actividad solo actual"
                value={formatInteger(
                  summary.entered_current_customers,
                )}
                detail="Actividad actual sin actividad comparada"
              />
            )}

            {hasComparison && (
              <SummaryMetric
                label="Sin actividad actual"
                value={formatInteger(
                  summary.missing_current_customers,
                )}
                detail="Actividad comparada sin actividad actual"
              />
            )}

            <SummaryMetric
              label="Última venta"
              value={formatDate(summary.last_sale_date)}
              detail={
                summary.days_since_last_sale !== null
                  ? `${formatInteger(
                      summary.days_since_last_sale,
                    )} días desde la última venta`
                  : undefined
              }
            />
          </Box>
        )}

        {!portfolioAnalysis
          && data
          && summary
          && data.status === 'ok' && (
          <>
            <Divider sx={{ my: 2 }} />

            <Stack spacing={0.4}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: '#f8fafc',
                  fontWeight: 850,
                }}
              >
                Qué clientes explican el movimiento
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: '#94a3b8',
                  lineHeight: 1.55,
                }}
              >
                Lectura factual del portfolio completo. No
                clasifica automáticamente riesgo, oportunidad
                ni causa.
              </Typography>
            </Stack>

            <Box
              sx={{
                mt: 1.5,
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, minmax(0, 1fr))',
                  xl: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 1,
              }}
            >
              {hasComparison && largestGrowth && (
                <SituationCard
                  title="Mayor crecimiento de ingresos"
                  value={largestGrowth.label}
                  detail={
                    `Δ ingresos ${formatSignedCurrency(
                      largestGrowth.revenue_delta,
                      currencyCode,
                    )}. Ingresos actuales ${formatCurrency(
                      largestGrowth.current_revenue,
                      currencyCode,
                    )}.`
                  }
                  actionLabel="Ver crecimientos"
                  onAction={() =>
                    setAnalysisState({
                      productCustomerSort: 'revenue_delta',
                      productCustomerDirection: 'desc',
                    })
                  }
                />
              )}

              {hasComparison && largestDecline && (
                <SituationCard
                  title="Mayor caída de ingresos"
                  value={largestDecline.label}
                  detail={
                    `Δ ingresos ${formatSignedCurrency(
                      largestDecline.revenue_delta,
                      currencyCode,
                    )}. Ingresos actuales ${formatCurrency(
                      largestDecline.current_revenue,
                      currencyCode,
                    )}.`
                  }
                  actionLabel="Ver caídas"
                  onAction={() =>
                    setAnalysisState({
                      productCustomerSort: 'revenue_delta',
                      productCustomerDirection: 'asc',
                    })
                  }
                />
              )}

              {hasComparison && (
                <SituationCard
                  title="Cambio de actividad"
                  value={
                    `${formatInteger(
                      summary.entered_current_customers,
                    )} solo actual · ${formatInteger(
                      summary.missing_current_customers,
                    )} sin actividad actual`
                  }
                  detail={
                    'Describe actividad observada entre ambos '
                    + 'periodos. No clasifica clientes como '
                    + 'nuevos o perdidos.'
                  }
                  actionLabel="Explorar actividad"
                  onAction={() =>
                    setAnalysisState({
                      productCustomerSort: 'days_since_last_sale',
                      productCustomerDirection: 'desc',
                    })
                  }
                />
              )}

              <SituationCard
                title="Concentración actual"
                value={`Top 5 · ${formatPct(
                  summary.current_top_5_revenue_share_pct,
                )}`}
                detail={
                  `El mayor cliente representa ${formatPct(
                    summary.current_top_customer_revenue_share_pct,
                  )} de los ingresos actuales del producto.`
                }
                actionLabel="Explorar concentración"
                onAction={() =>
                  setAnalysisState({
                    productCustomerSort: 'revenue_share_pct',
                    productCustomerDirection: 'desc',
                  })
                }
              />
            </Box>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Stack spacing={0.35} sx={{ mb: 1.5 }}>
          <Typography
            variant="subtitle1"
            sx={{
              color: '#f8fafc',
              fontWeight: 900,
            }}
          >
            Explorar clientes y relaciones
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: '#94a3b8',
            }}
          >
            Profundiza en cualquier cliente después de revisar
            la estructura completa de la cartera.
          </Typography>
        </Stack>

        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', lg: 'center' }}
          justifyContent="space-between"
        >
          <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            flexWrap="wrap"
          >
            <QuickSortButton
              label="Mayor crecimiento"
              field="revenue_delta"
              direction="desc"
              activeField={sortField}
              activeDirection={sortDirection}
              disabled={!hasComparison}
              onSelect={(field, direction) =>
                setAnalysisState({
                  productCustomerSort: field,
                  productCustomerDirection:
                    direction,
                })
              }
            />

            <QuickSortButton
              label="Mayor caída"
              field="revenue_delta"
              direction="asc"
              activeField={sortField}
              activeDirection={sortDirection}
              disabled={!hasComparison}
              onSelect={(field, direction) =>
                setAnalysisState({
                  productCustomerSort: field,
                  productCustomerDirection:
                    direction,
                })
              }
            />

            <QuickSortButton
              label="Menor margen %"
              field="margin_percentage"
              direction="asc"
              activeField={sortField}
              activeDirection={sortDirection}
              onSelect={(field, direction) =>
                setAnalysisState({
                  productCustomerSort: field,
                  productCustomerDirection:
                    direction,
                })
              }
            />

            <QuickSortButton
              label="Mayor peso"
              field="revenue_share_pct"
              direction="desc"
              activeField={sortField}
              activeDirection={sortDirection}
              onSelect={(field, direction) =>
                setAnalysisState({
                  productCustomerSort: field,
                  productCustomerDirection:
                    direction,
                })
              }
            />

            <QuickSortButton
              label="Más tiempo sin venta"
              field="days_since_last_sale"
              direction="desc"
              activeField={sortField}
              activeDirection={sortDirection}
              onSelect={(field, direction) =>
                setAnalysisState({
                  productCustomerSort: field,
                  productCustomerDirection:
                    direction,
                })
              }
            />
          </Stack>

          <TextField
            size="small"
            value={searchInput}
            onChange={(event) =>
              setSearchInput(event.target.value)
            }
            placeholder="Buscar cliente o ID"
            sx={{
              minWidth: {
                xs: '100%',
                lg: 320,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Box>

      {customersQuery.isFetching && (
        <LinearProgress />
      )}

      {customersQuery.isError && (
        <Box sx={{ px: 2.5, pb: 2.5 }}>
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => customersQuery.refetch()}
              >
                Reintentar
              </Button>
            }
          >
            No se pudieron cargar los clientes del producto.
          </Alert>
        </Box>
      )}

      {data?.status === 'no_results' && (
        <Box sx={{ px: 3, py: 5, textAlign: 'center' }}>
          <Typography
            variant="body1"
            sx={{
              color: '#e2e8f0',
              fontWeight: 750,
            }}
          >
            No hay clientes para esta selección
          </Typography>

          <Typography
            variant="body2"
            sx={{ mt: 0.5, color: '#94a3b8' }}
          >
            Cambia la búsqueda o el periodo para ampliar el
            análisis.
          </Typography>
        </Box>
      )}

      {data?.status === 'ok' && (
        <>
          <TableContainer>
            <Table
              size="small"
              sx={{
                minWidth: 1360,

                '& .MuiTableCell-root': {
                  py: 1.15,
                  px: 1.4,
                  verticalAlign: 'middle',
                  borderColor:
                    'rgba(148,163,184,0.12)',
                  color: '#cbd5e1',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <SortableHeader
                    field="customer_label"
                    label="Cliente"
                    activeField={sortField}
                    direction={sortDirection}
                    onSort={handleSort}
                    align="left"
                  />

                  <SortableHeader
                    field="revenue"
                    label="Ingresos"
                    activeField={sortField}
                    direction={sortDirection}
                    onSort={handleSort}
                  />

                  <SortableHeader
                    field="margin_amount"
                    label={
                      currencyCode === 'EUR'
                        ? 'Margen €'
                        : currencyCode
                          ? `Margen ${currencyCode}`
                          : 'Margen'
                    }
                    activeField={sortField}
                    direction={sortDirection}
                    onSort={handleSort}
                  />

                  <SortableHeader
                    field="margin_percentage"
                    label="Margen %"
                    activeField={sortField}
                    direction={sortDirection}
                    onSort={handleSort}
                  />

                  <SortableHeader
                    field="units"
                    label="Unidades"
                    activeField={sortField}
                    direction={sortDirection}
                    onSort={handleSort}
                  />

                  <SortableHeader
                    field="revenue_share_pct"
                    label="Peso ingresos"
                    activeField={sortField}
                    direction={sortDirection}
                    onSort={handleSort}
                  />

                  <SortableHeader
                    field="average_net_unit_price"
                    label="Precio neto medio"
                    activeField={sortField}
                    direction={sortDirection}
                    onSort={handleSort}
                  />

                  <SortableHeader
                    field="last_sale_date"
                    label="Actividad"
                    activeField={sortField}
                    direction={sortDirection}
                    onSort={handleSort}
                    help={
                      'Primera línea: última venta observada '
                      + 'y días transcurridos hasta el cierre '
                      + 'del periodo analizado. Segunda línea: '
                      + 'días distintos con venta y '
                      + 'transacciones, actual/comparación; '
                      + 'entre paréntesis se muestra la '
                      + 'variación de días con venta.'
                    }
                  />
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.relationship.customer_id}
                    hover
                    role="button"
                    tabIndex={0}
                    aria-label={`Abrir relación ${row.customer.label}`}
                    onClick={() =>
                      openRelationship(row.customer.customer_id)
                    }
                    onKeyDown={(event) => {
                      if (
                        event.key === 'Enter'
                        || event.key === ' '
                      ) {
                        event.preventDefault();
                        openRelationship(row.customer.customer_id);
                      }
                    }}
                    sx={{
                      cursor: 'pointer',
                      '&:focus-visible': {
                        outline: '2px solid rgba(56,189,248,0.7)',
                        outlineOffset: -2,
                      },
                    }}
                  >
                    <CustomerCell row={row} />

                    <MetricCell
                      value={formatCurrency(
                        row.current.revenue,
                        currencyCode,
                      )}
                      comparison={
                        hasComparison
                          ? formatCurrency(
                              row.comparison?.revenue,
                              currencyCode,
                            )
                          : undefined
                      }
                      change={
                        hasComparison
                          ? formatSignedPct(
                              row.changes?.revenue_delta_pct,
                            )
                          : undefined
                      }
                      changeValue={
                        row.changes?.revenue_delta_pct
                      }
                    />

                    <MetricCell
                      value={formatCurrency(
                        row.current.margin_amount,
                        currencyCode,
                      )}
                      comparison={
                        hasComparison
                          ? formatCurrency(
                              row.comparison
                                ?.margin_amount,
                              currencyCode,
                            )
                          : undefined
                      }
                      change={
                        hasComparison
                          ? formatSignedPct(
                              row.changes
                                ?.margin_amount_delta_pct,
                            )
                          : undefined
                      }
                      changeValue={
                        row.changes
                          ?.margin_amount_delta_pct
                      }
                    />

                    <MetricCell
                      value={formatPct(
                        row.current.margin_percentage,
                      )}
                      comparison={
                        hasComparison
                          ? formatPct(
                              row.comparison
                                ?.margin_percentage,
                            )
                          : undefined
                      }
                      change={
                        hasComparison
                          ? formatSignedPoints(
                              row.changes
                                ?.margin_percentage_delta_points,
                            )
                          : undefined
                      }
                      changeValue={
                        row.changes
                          ?.margin_percentage_delta_points
                      }
                    />

                    <MetricCell
                      value={formatNumber(
                        row.current.units,
                      )}
                      comparison={
                        hasComparison
                          ? formatNumber(
                              row.comparison?.units,
                            )
                          : undefined
                      }
                      change={
                        hasComparison
                          ? formatSignedPct(
                              row.changes?.units_delta_pct,
                            )
                          : undefined
                      }
                      changeValue={
                        row.changes?.units_delta_pct
                      }
                    />

                    <MetricCell
                      value={formatPct(
                        row.current.revenue_share_pct,
                      )}
                      comparison={
                        hasComparison
                          ? formatPct(
                              row.comparison
                                ?.revenue_share_pct,
                            )
                          : undefined
                      }
                      change={
                        hasComparison
                          ? formatSignedPoints(
                              row.changes
                                ?.revenue_share_delta_points,
                            )
                          : undefined
                      }
                      changeValue={
                        row.changes
                          ?.revenue_share_delta_points
                      }
                    />

                    <MetricCell
                      value={formatCurrency(
                        row.current.average_net_unit_price,
                        currencyCode,
                      )}
                      comparison={
                        hasComparison
                          ? formatCurrency(
                              row.comparison
                                ?.average_net_unit_price,
                              currencyCode,
                            )
                          : undefined
                      }
                      change={
                        hasComparison
                          ? formatSignedPct(
                              row.changes
                                ?.average_net_unit_price_delta_pct,
                            )
                          : undefined
                      }
                      changeValue={
                        row.changes
                          ?.average_net_unit_price_delta_pct
                      }
                    />

                    <ActivityCell
                      row={row}
                      hasComparison={hasComparison}
                    />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider />

          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: '#64748b' }}
            >
              {data.page.returned_count} relaciones en esta
              página · paginación por cursor
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                disabled={
                  cursorHistory.length === 0
                  || customersQuery.isFetching
                }
                onClick={previousPage}
              >
                Anterior
              </Button>

              <Button
                size="small"
                variant="contained"
                disabled={
                  !data.page.has_more
                  || !data.page.next_cursor
                  || customersQuery.isFetching
                }
                onClick={nextPage}
              >
                Siguiente
              </Button>
            </Stack>
          </Box>
        </>
      )}

      {selectedRelationshipCustomerId && (
        <CustomerRelationshipWorkspace
          customerId={selectedRelationshipCustomerId}
          productId={productId}
          startDate={startDate}
          endDate={endDate}
          comparisonMode={comparisonMode}
          comparisonStartDate={comparisonStartDate}
          comparisonEndDate={comparisonEndDate}
          timeGrain={timeGrain}
          onClose={closeRelationship}
        />
      )}
      <PricingDecisionExistingPairWorkspace
        open={decisionWorkspaceOpen}
        onClose={() =>
          setDecisionWorkspaceOpen(false)
        }
        onOpenObservedRelationship={(
          relationshipCustomerId,
        ) => {
          setDecisionWorkspaceOpen(
            false,
          );

          openRelationship(
            relationshipCustomerId,
          );
        }}
        source="product"
        customerId={null}
        productId={productId}
        startDate={startDate}
        endDate={endDate}
        comparisonMode={comparisonMode}
        comparisonStartDate={comparisonStartDate}
        comparisonEndDate={comparisonEndDate}
        timeGrain={timeGrain}
        currencyCode={currencyCode}
      />
    </Paper>
  );
}
